import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import BenchmarkVerdict from './BenchmarkVerdict';
import { SavingsAndCoverage, QueueQuality } from './SummaryPanels';
import TriageSimulator, { DEFAULT_MINUTES_PER_ALERT } from './TriageSimulator';
import TierMigrationChart from './TierMigrationChart';
import ScenarioPerformance from './ScenarioPerformance';
import RiskDriversExplainer from './RiskDriversExplainer';
import RescuedRisksTable from './RescuedRisksTable';
import RescuedCaseDrawer from './RescuedCaseDrawer';
import RegulatoryDossierModal from './RegulatoryDossierModal';
import { RankingQualityPanel, StabilityPanel } from './EvidencePanels';
import { PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { count, hasValue } from './BenchmarkVocabulary';
import { suggestPolicy } from './benchmarkModel';
import {
    BenchmarkRequestError, BenchmarkRun, DataSource, DimensionsResponse, DispositionMatrixResponse,
    ExecutiveSummaryResponse, RegulatoryDossierResponse, RiskDriversResponse, ScoredAlert, SuppliedResultRow,
    TypologyPerformanceResponse, WorkloadSimulatorResponse, benchmarkService, isInvestigationRun
} from '../../services/benchmarkService';
import InvestigationBenchmarkView, {
    INVESTIGATION_TABS, InvestigationTabKey, TAB_FOR_TYPE, orderedOutcomes
} from './investigation/InvestigationBenchmarkView';
import InvestigationDrawer from './investigation/InvestigationDrawer';
import { agentSlugName } from './investigation/investigationVocabulary';
import { keepingNavState } from '../../utils/searchParamsNavigation';
import { benchmarkFamily, flowFamily, suppliedSampleFamilies, workflowCode } from './benchmarkFlowCodes';
import { isIndicative } from './benchmarkEvidence';
import { buildWithheldIndex, isKycPointInTime } from './withheldMetrics';
import { IndicativeBanner } from './BenchmarkQualifiers';
import SuppliedSampleBanner from './supplied/SuppliedSampleBanner';
import ProvenanceDrawer from './supplied/ProvenanceDrawer';
import RunPickerDrawer from './supplied/RunPickerDrawer';
import SuppliedResultsTab from './supplied/results/SuppliedResultsTab';
import CaseWhyDrawer from './supplied/results/CaseWhyDrawer';
import RunStatusPanel from './supplied/results/RunStatusPanel';
import { useRunReference } from './supplied/results/useRunReference';
import { backTarget } from '../flowEditorNavigation';
import { dsVar } from '../../theme/tokens';

const TABS = [
    // Named for the question each answers. "Summary" said nothing about what was
    // being summarised, and "What changes" read as a changelog rather than the
    // operational effect on the alert queue.
    //
    // Impact leads, so the page opens on what the agent did to the queue before
    // it argues for adopting it.
    //
    // A supplied sample opens on its own results instead: the user wants to know
    // what happened to their alerts, and the executive tabs make caseload-wide
    // claims a small curated sample cannot support. Shown for supplied runs only.
    { key: 'results', label: "Your sample's results" },
    { key: 'changes', label: 'Operational impact' },
    { key: 'summary', label: 'Business case' },
    { key: 'evidence', label: 'Evidence' }
] as const;

type PriorityTabKey = typeof TABS[number]['key'];

/**
 * An investigation run has its own tabs: the priority tabs describe a
 * re-ordered alert queue, which it does not produce, and their endpoints
 * refuse it (409 not_applicable).
 */
type TabKey = PriorityTabKey | InvestigationTabKey;

/** Which resources each priority tab needs, so a tab only pays for its own requests. */
const TAB_RESOURCES: Record<PriorityTabKey, ResourceKey[]> = {
    results: ['matrix'],
    summary: ['summary', 'simulator'],
    changes: ['matrix', 'typology'],
    evidence: ['summary', 'typology', 'drivers', 'dossier']
};

type ResourceKey = 'summary' | 'simulator' | 'matrix' | 'typology' | 'drivers' | 'dossier';

interface ResourceState {
    summary: ExecutiveSummaryResponse | null;
    simulator: WorkloadSimulatorResponse | null;
    matrix: DispositionMatrixResponse | null;
    typology: TypologyPerformanceResponse | null;
    drivers: RiskDriversResponse | null;
    dossier: RegulatoryDossierResponse | null;
}

const EMPTY_RESOURCES: ResourceState = {
    summary: null, simulator: null, matrix: null, typology: null, drivers: null, dossier: null
};

/** The docked panel beside the results. One at a time: they share the same column. */
type OpenDrawer = 'case' | 'why' | 'investigation' | 'provenance' | 'runs' | null;

/**
 * Benchmark results for one test run.
 *
 * Structured as an answer, then what changes, then the evidence — because the
 * reader is an AML operations lead deciding whether to change how alerts are
 * triaged, not a data scientist reading an evaluation report. Everything
 * technical still exists; it sits on the Evidence tab.
 *
 * Previously a single un-tabbed scroll of seven dense panels led by ROC AUC.
 */
const BenchmarkDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { runKey: urlRunKey } = useParams<{ runKey?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const navState = location.state || {};
    const initialFlowCode = navState.flow_template_code || 'BENCHMARK_PRIORITIZE_SUSPICIOUS_ACTIVITY_ALERTS';
    const initialProjectId = navState.project_id || 'benchmark';

    /**
     * A submission not yet resolved to a run: the editor opens the page with the
     * referenceId it submitted under, before the run row exists.
     */
    const referenceId = !urlRunKey ? searchParams.get('ref') : null;
    const reference = useRunReference(referenceId);

    const [run, setRun] = useState<BenchmarkRun | null>(null);
    const [runError, setRunError] = useState<string | null>(null);
    const [runLoading, setRunLoading] = useState(true);

    const investigation = isInvestigationRun(run);

    /** The Results tab exists only for a supplied sample's run; an investigation run has its own set. */
    const visibleTabs = useMemo<readonly { key: TabKey; label: string }[]>(
        () => (isInvestigationRun(run)
            ? INVESTIGATION_TABS
            : TABS.filter((t) => t.key !== 'results' || run?.dataSource === 'user_supplied')),
        [run]
    );
    const activeTab: TabKey = (visibleTabs.find((t) => t.key === searchParams.get('tab'))?.key) || visibleTabs[0].key;

    /** An investigation run's comparisons: one request serves every one of its tabs. */
    const [dimensions, setDimensions] = useState<DimensionsResponse | null>(null);
    const [dimensionsLoading, setDimensionsLoading] = useState(false);
    const [dimensionsError, setDimensionsError] = useState<string | null>(null);

    const loadDimensions = useCallback(async () => {
        if (!run?.runKey || !isInvestigationRun(run)) {
            setDimensions(null);
            return;
        }
        setDimensionsLoading(true);
        setDimensionsError(null);
        try {
            setDimensions(await benchmarkService.getDimensions(run.runKey));
        } catch (err) {
            setDimensionsError(err instanceof BenchmarkRequestError ? err.message : 'Could not load this run\'s comparisons.');
        } finally {
            setDimensionsLoading(false);
        }
    }, [run]);

    useEffect(() => { loadDimensions(); }, [loadDimensions]);

    /** The run's dimensions by key, for the drawer's labels. */
    const manifest = useMemo(
        () => new Map((dimensions?.dimensions ?? []).map((d) => [d.dimensionKey, d])),
        [dimensions]
    );

    const [resources, setResources] = useState<ResourceState>(EMPTY_RESOURCES);
    const [errors, setErrors] = useState<Partial<Record<ResourceKey, string>>>({});
    const [pending, setPending] = useState<Set<ResourceKey>>(new Set());
    const loadedRef = useRef<Set<ResourceKey>>(new Set());

    const [selectedAlert, setSelectedAlert] = useState<ScoredAlert | null>(null);
    const [selectedCaseRef, setSelectedCaseRef] = useState<string | null>(null);
    const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(null);
    const [dossierOpen, setDossierOpen] = useState(false);

    /**
     * The scorecard item open in the investigation drawer: the URL's item, while its
     * tab is the one showing and that tab is a scorecard. Kept in the URL so the
     * open item survives a reload or a shared link. The Decisions tab shows its
     * items in full on the page, so its item is only scrolled to.
     */
    const itemKey = searchParams.get('item');
    const drawerDimension = useMemo(() => {
        const d = investigation && itemKey ? manifest.get(itemKey) : undefined;
        return d && TAB_FOR_TYPE[d.dimensionType] === activeTab && activeTab !== 'decisions' ? d : null;
    }, [investigation, itemKey, manifest, activeTab]);

    /**
     * The review level the business case is stated at.
     *
     * Owned here rather than inside the slider so the savings and coverage
     * figures describe the level the reader has actually selected. Null until
     * the simulator loads, at which point it seeds from the recommendation —
     * so the page opens on the level the recommendation names instead of an
     * arbitrary 55%.
     */
    const [reviewPercent, setReviewPercent] = useState<number | null>(null);
    const [minutesPerAlert, setMinutesPerAlert] = useState(DEFAULT_MINUTES_PER_ALERT);

    const recommended = useMemo(
        () => (resources.simulator ? suggestPolicy(resources.simulator.reviewPolicies) : null),
        [resources.simulator]
    );

    useEffect(() => {
        if (reviewPercent !== null || !recommended) return;
        const fraction = recommended.policy.reviewFraction;
        if (hasValue(fraction)) setReviewPercent(Number((fraction * 100).toFixed(1)));
    }, [recommended, reviewPercent]);

    const effectiveReviewPercent = reviewPercent ?? 55;
    const atRecommendedLevel = Boolean(
        recommended
        && hasValue(recommended.policy.reviewFraction)
        && Math.abs((recommended.policy.reviewFraction as number) * 100 - effectiveReviewPercent) < 0.5
    );

    /**
     * Load the run the URL names.
     *
     * Previously this fetched the CURRENT run and then overwrote its runKey with
     * the one from the URL, so a deep link to an older run showed that run's
     * charts beneath the current run's id, org and timestamps.
     */
    const loadRun = useCallback(async () => {
        // Nothing to load until the submission has produced a run.
        if (referenceId) {
            setRun(null);
            setRunLoading(false);
            return;
        }
        setRunLoading(true);
        setRunError(null);
        try {
            const keyNum = urlRunKey ? parseInt(urlRunKey, 10) : NaN;
            const resolved = Number.isFinite(keyNum)
                ? await benchmarkService.getRun(keyNum)
                // By family, so a published build's stamped run is found.
                : await benchmarkService.getCurrentRun({
                    flowFamily: flowFamily(initialFlowCode),
                    projectId: initialProjectId
                });

            if (!resolved) {
                setRunError('This workflow has not been tested yet, so there are no results to show.');
                setRun(null);
                return;
            }
            setRun(resolved);
        } catch (err) {
            setRunError(err instanceof BenchmarkRequestError ? err.message : 'Could not load this test run.');
            setRun(null);
        } finally {
            setRunLoading(false);
        }
    }, [urlRunKey, referenceId, initialFlowCode, initialProjectId]);

    useEffect(() => {
        loadedRef.current = new Set();
        setResources(EMPTY_RESOURCES);
        setErrors({});
        loadRun();
    }, [loadRun]);

    const fetchers: Record<ResourceKey, (key: number) => Promise<any>> = useMemo(() => ({
        summary: (k) => benchmarkService.getExecutiveSummary(k),
        simulator: (k) => benchmarkService.getWorkloadSimulator(k),
        matrix: (k) => benchmarkService.getDispositionMatrix(k),
        typology: (k) => benchmarkService.getTypologyPerformance(k),
        drivers: (k) => benchmarkService.getRiskDrivers(k),
        dossier: (k) => benchmarkService.getRegulatoryDossier(k)
    }), []);

    /**
     * Fetches what the active tab needs, independently.
     *
     * Each resource settles on its own — the previous build used Promise.all, so
     * one failing endpoint left all six panels blank with nothing on screen to
     * say why.
     */
    const loadResources = useCallback(async (keys: ResourceKey[], force = false) => {
        if (!run?.runKey) return;
        const wanted = keys.filter((k) => force || !loadedRef.current.has(k));
        if (wanted.length === 0) return;

        wanted.forEach((k) => loadedRef.current.add(k));
        setPending((prev) => new Set([...prev, ...wanted]));

        const settled = await Promise.allSettled(wanted.map((k) => fetchers[k](run.runKey)));

        setResources((prev) => {
            const next = { ...prev };
            settled.forEach((result, i) => {
                if (result.status === 'fulfilled') next[wanted[i]] = result.value;
            });
            return next;
        });
        setErrors((prev) => {
            const next = { ...prev };
            settled.forEach((result, i) => {
                const key = wanted[i];
                if (result.status === 'rejected') {
                    const reason = result.reason;
                    next[key] = reason instanceof BenchmarkRequestError
                        ? reason.message
                        : 'Could not load this section.';
                    // Let a retry try again.
                    loadedRef.current.delete(key);
                } else {
                    delete next[key];
                }
            });
            return next;
        });
        setPending((prev) => {
            const next = new Set(prev);
            wanted.forEach((k) => next.delete(k));
            return next;
        });
    }, [run, fetchers]);

    /** The priority tabs' resources; an investigation run reads its dimensions instead. */
    const priorityResources = (tab: TabKey): ResourceKey[] =>
        (investigation ? [] : TAB_RESOURCES[tab as PriorityTabKey] ?? []);

    useEffect(() => {
        if (isInvestigationRun(run)) return;
        loadResources(TAB_RESOURCES[activeTab as PriorityTabKey] ?? []);
    }, [activeTab, loadResources, run]);

    /** Once the submission has been scored, open its run in place of the reference. */
    useEffect(() => {
        if (reference.status?.status !== 'complete') return;
        navigate(`/benchmark-dashboard/${reference.status.runKey}?tab=results`, { replace: true, state: location.state });
    }, [reference.status, navigate, location.state]);

    const retry = (key: ResourceKey) => loadResources([key], true);

    /** What the run declined to measure, or measured with a caveat. */
    const withheld = useMemo(() => buildWithheldIndex(run?.withheldMetrics), [run]);
    const kycPointInTime = useMemo(() => isKycPointInTime(run, withheld), [run, withheld]);
    const isSupplied = run?.dataSource === 'user_supplied';

    /**
     * How much evidence stands behind the run's figures. The run's own case
     * count stands in until the summary loads, so every tab can say so.
     */
    const runEvidence = resources.summary?.evidence
        ?? (run ? { caseCount: run.predictionCount, confidence: null } : null);
    // An investigation run states its support per dimension; the run-wide banner's
    // copy (triage, staffing) describes a priority run.
    const indicative = !investigation && isIndicative(runEvidence);

    /** The workflow this run belongs to, whichever flow recorded it. */
    const workflow = navState.base_flow_template_code
        || (run?.flowTemplateCode ? workflowCode(run.flowTemplateCode) : workflowCode(initialFlowCode));

    const refreshAll = () => {
        loadedRef.current = new Set();
        loadRun();
        if (investigation) loadDimensions();
        else loadResources(priorityResources(activeTab), true);
    };

    const setTab = (tab: TabKey) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        // An item picked from Evidence → Coverage is for the tab it opened, not the next one.
        next.delete('item');
        // Filters belong to the alerts table; leaving them set while another tab
        // is open would apply them invisibly when the reader comes back.
        //
        // keepingNavState because the bare options object would clear
        // location.state, and `returnTo` lives there — see the helper.
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };

    /**
     * Opens one compared item on its tab: a scorecard's in the drawer, a decision by
     * scrolling to it. From Evidence → Coverage it is pushed, so Back returns the
     * reader to the coverage list they came from; a row on the same tab replaces,
     * so stepping through rows does not fill the history.
     */
    const openInvestigationItem = (tab: InvestigationTabKey, dimensionKey: string) => {
        // The item takes the drawer's place, so any case open over the last one goes.
        setSelectedCaseRef(null);
        setOpenDrawer((current) => (current === 'investigation' ? null : current));
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        next.set('item', dimensionKey);
        setSearchParams(next, keepingNavState(location.state, { replace: tab === activeTab }));
    };

    /** Drops the open item from the URL, so the drawer no longer shows it. */
    const clearInvestigationItem = () => {
        if (!searchParams.has('item')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('item');
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };

    /** Filters for the alerts table, kept in the query string so a view is linkable. */
    const tableFilters = useMemo(() => {
        const entries = Object.fromEntries(searchParams.entries());
        delete entries.tab;
        delete entries.item;
        return entries;
    }, [searchParams]);

    /** Scenario filter options come from the run rather than a hardcoded list. */
    const typologyCodes = useMemo(
        () => (resources.typology?.typologies ?? []).map((t) => t.segmentCode).filter(Boolean),
        [resources.typology]
    );

    /** Where the opener said Back should go, if it said. */
    const backTo = backTarget(navState);

    /**
     * Returns to whatever opened these results.
     *
     * This navigated to the flow editor unconditionally, which was right while
     * the editor's Test button was the only way in. A project page card can now
     * open a run directly, and sending that reader to the editor drops them on
     * a page they have never seen.
     *
     * The opener says where Back goes, in the same `returnTo` the editor uses,
     * so the chain unwinds one level at a time.
     */
    const handleBack = () => {
        if (backTo) {
            navigate(backTo.pathname, backTo.state ? { state: backTo.state } : undefined);
            return;
        }

        // Nothing recorded — a pasted link, or a caller predating returnTo. The
        // flow this run belongs to is the most useful place to land, and the run
        // names it even when nothing else does.
        const baseCode = workflow || undefined;

        navigate('/AIStudio/customize', {
            state: {
                flow_template_code: baseCode,
                operator_name: navState.operator_name,
                operator_id: navState.operator_id,
                fromOperator: navState.fromOperator,
                campaign_id: navState.campaign_id,
                project_id: navState.project_id
            }
        });
    };

    const isPending = (key: ResourceKey) => pending.has(key);
    const drawerOpen = openDrawer !== null || drawerDimension !== null;

    /**
     * Whether the investigation drawer is at its wide width, as on Campaign Insights.
     * The page owns it so it holds while the drawer moves between items and cases,
     * and it resets once the drawer closes.
     */
    const [drawerWide, setDrawerWide] = useState(false);
    const toggleDrawerWide = useCallback(() => setDrawerWide((wide) => !wide), []);
    useEffect(() => {
        if (!drawerOpen) setDrawerWide(false);
    }, [drawerOpen]);

    const selectAlert = (alert: ScoredAlert | null) => {
        setSelectedAlert(alert);
        setSelectedCaseRef(null);
        setOpenDrawer(alert ? 'case' : null);
    };

    /** A supplied alert's row: opens why its case got its tier. */
    const selectResult = (row: SuppliedResultRow) => {
        setSelectedAlert(null);
        setSelectedCaseRef(row.caseRef);
        setOpenDrawer('why');
    };

    /**
     * A replayed case of an investigation run: opens its comparisons across every
     * dimension, in place of the item it was picked from when one is open.
     */
    const selectInvestigationCase = (caseRef: string) => {
        setSelectedAlert(null);
        setSelectedCaseRef(caseRef);
        setOpenDrawer('investigation');
    };

    /** Back from a case to the item it was opened from. */
    const backToInvestigationItem = () => {
        setSelectedCaseRef(null);
        setOpenDrawer(null);
    };

    /** A panel other than the case drawers; the selected case and open item are let go. */
    const showPanel = (panel: 'provenance' | 'runs') => {
        setSelectedAlert(null);
        setSelectedCaseRef(null);
        clearInvestigationItem();
        setOpenDrawer((current) => (current === panel ? null : panel));
    };

    const closeDrawer = () => {
        setOpenDrawer(null);
        setSelectedAlert(null);
        setSelectedCaseRef(null);
        if (drawerDimension) clearInvestigationItem();
    };

    /**
     * Back to the workflow with the sample upload open, to correct the file.
     * The editor opens its wizard when it sees `openSampleWizard`.
     */
    const resubmit = () => {
        if (backTo) {
            navigate(backTo.pathname, { state: { ...(backTo.state || {}), openSampleWizard: true } });
            return;
        }
        navigate('/AIStudio/customize', {
            state: {
                flow_template_code: workflow || undefined,
                campaign_id: navState.campaign_id,
                project_id: navState.project_id,
                openSampleWizard: true
            }
        });
    };

    /**
     * Opens another run in place. The table's filters belong to the run being
     * left, so only the tab carries over; the opener's navigation state does,
     * so Back still returns to wherever the dashboard was opened from.
     *
     * A supplied sample opens on its results, as it does from everywhere else.
     */
    const openRun = (runKey: number, dataSource: DataSource) => {
        const tab = dataSource === 'user_supplied'
            ? 'results'
            : activeTab === 'results' ? 'changes' : activeTab;
        navigate(`/benchmark-dashboard/${runKey}?tab=${tab}`, { state: location.state });
    };

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="marketplace" forceCollapsed={drawerOpen} />

            <div className="flex flex-1 flex-col overflow-hidden">
                <AppHeader
                    left={
                        <Button
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                            sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.875rem' }}
                        >
                            {navState.returnTo?.label || 'Back to workflow'}
                        </Button>
                    }
                />

                <main className="flex-1 overflow-y-auto px-8 pb-16">
                    <Box sx={{ maxWidth: '78rem', mx: 'auto', pt: 4 }}>
                        <Box className="flex flex-wrap items-start justify-between gap-4">
                            <Box>
                                <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 600, color: dsVar('fg') }}>
                                    {isSupplied || referenceId ? 'Supplied-sample results' : 'Backtest results'}
                                </Typography>
                                {/*
                                  What was tested, in a sentence. The old header
                                  showed a mono breadcrumb reading
                                  "/ AML COMPLIANCE CONSOLE > BENCHMARK RUN (…)"
                                  and a caseload chip whose period was a literal.
                                */}
                                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2'), mt: 0.75, maxWidth: '46rem', lineHeight: 1.6 }}>
                                    {referenceId
                                        ? 'Your sample has been submitted. Its results open here as soon as they are written.'
                                        : run && investigation && run.comparisonSpec?.stage === 'edd'
                                        ? `We re-performed ${count(run.predictionCount)} enhanced due diligence reviews your team has already concluded${run.agentSelection?.agents?.length ? ` with ${run.agentSelection.agents.map((a) => agentSlugName(a)).join(', ')}` : ''}, then compared the checks' findings, the risk rating and the decision to retain or report with the historical record.`
                                        : run && investigation
                                        ? `We re-investigated ${count(run.predictionCount)} cases your team has already closed${run.agentSelection?.agents?.length ? ` with ${run.agentSelection.agents.map((a) => agentSlugName(a)).join(', ')}` : ''}, then compared the agents' findings and decisions with the historical record.`
                                        : run && isSupplied
                                        ? `We re-prioritised ${count(run.predictionCount)} alerts from a sample you supplied, then compared both your current rules and the agent against the outcomes recorded in it.`
                                        : run
                                        ? `We re-prioritised ${count(run.predictionCount)} alerts your team has already worked, then compared both your current rules and the agent against what investigators actually found.`
                                        : 'Comparing your current alert rules against the agent, using cases your team has already closed.'}
                                </Typography>
                            </Box>

                            <Box className="flex items-center gap-2">
                                <Button
                                    onClick={() => showPanel('runs')}
                                    startIcon={<SwapHorizIcon sx={{ fontSize: 18 }} />}
                                    sx={{
                                        color: dsVar('fg-body'), textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600,
                                        border: `1px solid ${dsVar('line')}`, borderRadius: '8px', px: 1.5,
                                        '&:hover': { borderColor: dsVar('accent-fg'), backgroundColor: 'rgba(255, 222, 89, 0.06)' }
                                    }}
                                >
                                    Switch run
                                </Button>
                                <Button
                                    onClick={refreshAll}
                                    startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                                    sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.8125rem' }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>

                        {referenceId ? (
                            <Box sx={{ mt: 4 }}>
                                <RunStatusPanel
                                    status={reference.status}
                                    latestStep={reference.latestStep}
                                    error={reference.error}
                                    onRetry={reference.retry}
                                    onResubmit={resubmit}
                                />
                            </Box>
                        ) : runError ? (
                            <Box sx={{ mt: 4 }}>
                                <PanelError message={runError} onRetry={loadRun} />
                            </Box>
                        ) : runLoading ? (
                            <Box sx={{ mt: 4 }}><PanelSkeleton height={420} /></Box>
                        ) : (
                            <>
                                {run && (
                                    <SuppliedSampleBanner
                                        run={run}
                                        onOpenProvenance={() => showPanel('provenance')}
                                    />
                                )}
                                {indicative && (
                                    <Box sx={{ mt: 2 }}>
                                        <IndicativeBanner evidence={runEvidence} />
                                    </Box>
                                )}

                                <Tabs
                                    value={activeTab}
                                    onChange={(_, value) => setTab(value as TabKey)}
                                    sx={{
                                        borderBottom: `1px solid ${dsVar('line')}`, mt: 3, mb: 3,
                                        '& .MuiTab-root': {
                                            textTransform: 'none', fontSize: '0.875rem', fontWeight: 500,
                                            color: dsVar('fg-2'), '&.Mui-selected': { color: dsVar('accent-fg') }
                                        },
                                        '& .MuiTabs-indicator': { backgroundColor: dsVar('accent') }
                                    }}
                                >
                                    {visibleTabs.map((tab) => (
                                        <Tab key={tab.key} value={tab.key} label={tab.label} />
                                    ))}
                                </Tabs>

                                {investigation && run && (
                                    <InvestigationBenchmarkView
                                        run={run}
                                        tab={activeTab as InvestigationTabKey}
                                        dimensions={dimensions}
                                        loading={dimensionsLoading}
                                        error={dimensionsError}
                                        onRetry={loadDimensions}
                                        withheld={withheld}
                                        onCaseSelect={selectInvestigationCase}
                                        selectedCaseRef={openDrawer === 'investigation' ? selectedCaseRef : null}
                                        focusItem={searchParams.get('item')}
                                        onOpenItem={openInvestigationItem}
                                    />
                                )}

                                {!investigation && activeTab === 'results' && run && (
                                    <SuppliedResultsTab
                                        run={run}
                                        matrix={resources.matrix}
                                        matrixLoading={isPending('matrix')}
                                        matrixError={errors.matrix}
                                        onRetryMatrix={() => retry('matrix')}
                                        withheld={withheld}
                                        onRowSelect={selectResult}
                                        selectedCaseRef={openDrawer === 'why' ? selectedCaseRef : null}
                                    />
                                )}

                                {!investigation && activeTab === 'summary' && (
                                    <Box className="flex flex-col gap-6">
                                        <BenchmarkVerdict
                                            summary={resources.summary}
                                            simulator={resources.simulator}
                                            indicative={indicative}
                                            loading={isPending('summary') || isPending('simulator')}
                                        />

                                        {/*
                                          Says which level the figures below describe.
                                          Without it, moving the slider silently changes
                                          numbers further up the page.
                                        */}
                                        {resources.simulator && (
                                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), mt: -1 }}>
                                                {atRecommendedLevel
                                                    ? 'At the recommended level:'
                                                    : `At the level you have selected — reviewing the top ${effectiveReviewPercent.toFixed(1)}% of the queue:`}
                                            </Typography>
                                        )}

                                        <SavingsAndCoverage
                                            summary={resources.summary}
                                            simulator={resources.simulator}
                                            reviewFraction={effectiveReviewPercent / 100}
                                            minutesPerAlert={minutesPerAlert}
                                            indicative={indicative}
                                            loading={isPending('summary') || isPending('simulator')}
                                            error={errors.summary || errors.simulator}
                                            onRetry={() => retry(errors.summary ? 'summary' : 'simulator')}
                                        />

                                        <QueueQuality
                                            summary={resources.summary}
                                            indicative={indicative}
                                            loading={isPending('summary')}
                                            error={errors.summary}
                                            onRetry={() => retry('summary')}
                                        />

                                        <TriageSimulator
                                            simulator={resources.simulator}
                                            reviewPercent={effectiveReviewPercent}
                                            onReviewPercentChange={setReviewPercent}
                                            minutesPerAlert={minutesPerAlert}
                                            onMinutesPerAlertChange={setMinutesPerAlert}
                                            indicative={isIndicative(resources.simulator?.evidence ?? runEvidence)}
                                            loading={isPending('simulator')}
                                            error={errors.simulator}
                                            onRetry={() => retry('simulator')}
                                        />
                                    </Box>
                                )}

                                {!investigation && activeTab === 'changes' && (
                                    <Box className="flex flex-col gap-6">
                                        {/*
                                          The whole-queue picture first: how many alerts
                                          changed priority and in which direction. The
                                          individual cases below are the evidence for it,
                                          and read as a list of anecdotes without it.
                                        */}
                                        <TierMigrationChart
                                            matrix={resources.matrix}
                                            withheld={withheld}
                                            loading={isPending('matrix')}
                                            error={errors.matrix}
                                            onRetry={() => retry('matrix')}
                                        />

                                        <SectionCard
                                            title="Risks your current rules would have missed"
                                            subtitle="Alerts your rules placed in a low priority that the agent raised, and investigators confirmed. Select one to see why."
                                            accent="#FFDE59"
                                        >
                                            {run?.runKey ? (
                                                <RescuedRisksTable
                                                    runKey={run.runKey}
                                                    filters={tableFilters}
                                                    onRowSelect={selectAlert}
                                                    selectedAlertId={selectedAlert?.alertId ?? null}
                                                    typologyCodes={typologyCodes}
                                                />
                                            ) : null}
                                        </SectionCard>
                                        <ScenarioPerformance
                                            performance={resources.typology}
                                            withheld={withheld}
                                            loading={isPending('typology')}
                                            error={errors.typology}
                                            onRetry={() => retry('typology')}
                                        />
                                    </Box>
                                )}

                                {!investigation && activeTab === 'evidence' && (
                                    <Box className="flex flex-col gap-6">
                                        <RankingQualityPanel
                                            summary={resources.summary}
                                            run={run}
                                            withheld={withheld}
                                            loading={isPending('summary')}
                                            error={errors.summary}
                                            onRetry={() => retry('summary')}
                                        />
                                        <StabilityPanel
                                            performance={resources.typology}
                                            withheld={withheld}
                                            loading={isPending('typology')}
                                            error={errors.typology}
                                            onRetry={() => retry('typology')}
                                        />
                                        <RiskDriversExplainer
                                            drivers={resources.drivers}
                                            withheld={withheld}
                                            kycPointInTime={kycPointInTime}
                                            loading={isPending('drivers')}
                                            error={errors.drivers}
                                            onRetry={() => retry('drivers')}
                                        />
                                        <SectionCard
                                            title="Model governance record"
                                            subtitle="The scope, statistics and configuration digest for this run, for model validation and audit."
                                            icon={<GavelOutlinedIcon fontSize="small" />}
                                            accent="#FFDE59"
                                        >
                                            {errors.dossier ? (
                                                <PanelError message={errors.dossier} onRetry={() => retry('dossier')} />
                                            ) : (
                                                <Button
                                                    onClick={() => setDossierOpen(true)}
                                                    disabled={!resources.dossier}
                                                    variant="contained"
                                                    startIcon={<GavelOutlinedIcon sx={{ fontSize: 18 }} />}
                                                    sx={{
                                                        textTransform: 'none', fontWeight: 700, fontSize: '0.8125rem',
                                                        backgroundColor: dsVar('accent'), color: dsVar('on-accent'), borderRadius: '8px',
                                                        '&:hover': { backgroundColor: '#F5D149' },
                                                        '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') }
                                                    }}
                                                >
                                                    {resources.dossier ? 'Open governance record' : 'Loading…'}
                                                </Button>
                                            )}
                                        </SectionCard>
                                    </Box>
                                )}
                            </>
                        )}
                    </Box>
                </main>
            </div>

            {/* Flex children of the row container, which is what lets them dock. */}
            <RescuedCaseDrawer
                open={openDrawer === 'case'}
                alert={selectedAlert}
                onClose={closeDrawer}
            />
            <InvestigationDrawer
                runKey={investigation ? run?.runKey ?? null : null}
                dimension={drawerDimension}
                caseRef={openDrawer === 'investigation' ? selectedCaseRef : null}
                manifest={manifest}
                scenarios={dimensions?.scenarios ?? null}
                withheld={withheld}
                outcomes={orderedOutcomes(dimensions)}
                onCaseSelect={selectInvestigationCase}
                onBack={backToInvestigationItem}
                onClose={closeDrawer}
                expanded={drawerWide}
                onToggleExpand={toggleDrawerWide}
            />
            <CaseWhyDrawer
                open={openDrawer === 'why'}
                runKey={run?.runKey ?? null}
                caseRef={selectedCaseRef}
                onClose={closeDrawer}
            />
            <ProvenanceDrawer
                open={openDrawer === 'provenance'}
                run={run}
                onClose={closeDrawer}
            />
            <RunPickerDrawer
                open={openDrawer === 'runs'}
                onClose={closeDrawer}
                currentRun={run}
                stockFamily={benchmarkFamily(workflow)}
                suppliedFamilies={suppliedSampleFamilies(workflow)}
                projectId={run?.projectId || initialProjectId}
                onSelect={openRun}
            />

            <RegulatoryDossierModal
                open={dossierOpen}
                dossier={resources.dossier}
                onClose={() => setDossierOpen(false)}
            />
        </div>
    );
};

export default BenchmarkDashboard;
